import { PlusIcon } from '@heroicons/react/24/outline'
import { EyeIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/solid'
import * as _ from 'lodash-es'
import { observer } from 'mobx-react'
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { TextInput } from '@/components/forms/TextInput'
import { DownloadIcon, MapPinIcon, PencilIcon, TrashIcon } from '@/components/icons'
import { DataTable } from '@/components/shared/DataTable'
import { Button } from '@/components/ui/Button'
import useAbortControllers from '@/hooks/AbortControllers'
import useImpersonation from '@/hooks/Impersonation'
import { getDealerGroups } from '@/services/dealers.service'
import { createUserExport, getUserExport, getUsers } from '@/services/user.service'
import { DashboardStoreContext } from '@/stores/DashboardStore'
import { TaskStoreContext } from '@/stores/TaskStore'
import { UserStoreContext } from '@/stores/UserStore'
import { track } from '@/utils/analytics'
import { EXPORT_INITIATED } from '@/utils/analytics.constants'
import { formatDate, formatDateTime } from '@/utils/formatters'
import { handlePagination, toast } from '@/utils/helpers'

import { TAB_PARAM, USER_TAB, USER_TYPE } from './constants'
import DealerAccessCell from './DealerAccessCell'
import DealerUserModal from './DealerUserModal'
import DeleteUserModal from './DeleteDealerUserModal'
import RowActionButton from './RowActionButton'
import { canViewAsUser } from './rules'
import UserTabs from './UserTabs'

const USERS_BASE_URL = '/users/'
const USERS_PANEL_ID = 'users-table'

/**
 * A name to put in front of a person, falling back to the email.
 *
 * Plenty of rows have no first or last name — a WPC user who has never signed in has
 * neither — so a template string over the two renders "null null". The email is always
 * there and always identifies the row.
 */
const displayNameFor = (row) =>
  [row.firstName, row.lastName].filter(Boolean).join(' ').trim() || row.email

const TABS = [
  { id: USER_TAB.DEALER, label: 'Dealer Users' },
  { id: USER_TAB.WPC, label: 'WPC Users' },
]

/**
 *
 * Users
 *
 * WARP-939 — the admin Users page, toggling between dealer users and WPC users.
 *
 * The WPC side is read-only here. It exists so a WPC employee's access can be audited
 * from the portal instead of from SharePoint, and it is the surface the portal-managed
 * assignment work (WARP-922) builds its editing onto.
 */
const Users = observer(() => {
  // Context
  const { type } = useContext(DashboardStoreContext)
  const { createNewTask } = useContext(TaskStoreContext)
  const { canImpersonate, isImpersonating, isReadOnlySession, isWpcExecutive, user } =
    useContext(UserStoreContext)
  const { viewAs, loading: impersonating } = useImpersonation()

  // Adding, editing and deleting are writes, and a read-only session cannot make them.
  // The signed-in identity during impersonation is the person being viewed, so an
  // Executive viewing as an Executive would otherwise keep every control live and every
  // click would die in the axios guard with a toast.
  const canManageDealerUsers = isWpcExecutive && !isReadOnlySession
  // Nesting is refused by the server and blocked by the axios guard before it gets
  // there, and the identity deciding this during impersonation is the person being
  // viewed, so their role must not be what offers the button.
  const canStartViewAs = canImpersonate && !isImpersonating

  // Which side is showing lives in the URL, not in component state. It survives the
  // reload that ends a "View as" session, so exiting returns to the list the audit
  // started from instead of dropping back on Dealer Users — and it makes either list
  // something you can link to.
  const [searchParams, setSearchParams] = useSearchParams()
  const activeTab = searchParams.get(TAB_PARAM) === USER_TAB.WPC ? USER_TAB.WPC : USER_TAB.DEALER

  // State
  const [loading, setLoading] = useState(false)
  const [loadingExport, setLoadingExport] = useState(false)
  const [isDealerUserModalOpen, setIsDealerUserModalOpen] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [selectedUser, setSelectedUser] = useState(null)
  const [users, setUsers] = useState([])
  const [sortedColumn, setSortedColumn] = useState('first_name')
  const [dealerGroupOptions, setDealerGroupOptions] = useState([])
  const [updateTrigger, setUpdateTrigger] = useState(0)
  const [filter, setFilter] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')

  const isWpcTab = activeTab === USER_TAB.WPC

  // The WPC-managed / dealer-managed split is a property of dealers, so it narrows the
  // dealer user list only. WPC users reach dealers of both kinds.
  const typeOfDealers =
    type === 'dealer' && isWpcExecutive ? `?is_dealer_managed=true` : '?is_dealer_managed=false'
  const listParams = isWpcTab
    ? `user_type=${encodeURIComponent(USER_TYPE.WPC)}`
    : `is_dealer_managed=${type === 'dealer' && isWpcExecutive}`
  // Each list expands only the profile its own rows carry.
  const listExpand = isWpcTab
    ? 'expand=sso_user_profile'
    : 'expand=dealer_user_profile.dealers,dealer_user_profile.dealer_groups'

  // Pagination
  const [currentPage, setCurrentPage] = useState(1)
  // Flipped to send the table's own paginator back to page one; see handleTabChange.
  const [resetPagination, setResetPagination] = useState(false)
  const [perPage, setPerPage] = useState(20)
  const [pages, setPages] = useState(20)
  const [totalRows, setTotalRows] = useState()

  // Ref
  const searchInputRef = useRef()

  // Cancels superseded list requests so the last filter/page *requested* wins,
  // not the last to *resolve* (filter/sort/search/pagination races).
  const { getSignal } = useAbortControllers()

  const handleError = (message) => toast(message, 'error')

  useEffect(() => {
    if (isWpcTab) return

    const getDealerGroupsList = async () => {
      const response = await getDealerGroups(typeOfDealers)
      if (response) setDealerGroupOptions(response)
    }

    getDealerGroupsList()
  }, [type, isWpcTab])

  /**
   * Fetches the list of users from the given URL and updates the state with the response data.
   *
   * @param {string} url - The URL to fetch the users from.
   */
  const getUsersList = async (url) =>
    getUsers(
      url,
      handleError,
      setLoading,
      (response) => {
        setUsers(response.results)
        setTotalRows(response.count)
        setPages({ next: response.next, previous: response.previous })
      },
      getSignal('users'),
    )

  /**
   * Updates the search query based on `search`.
   * @param {string} search
   */
  const updateSearch = (search) => {
    let updatedFilter = ''
    if (search) updatedFilter = `q=${search}`

    setFilter(updatedFilter)
  }

  const filterUsers = useCallback(_.debounce(updateSearch, 500), [])

  /**
   * Switches sides of the tab.
   *
   * Everything that describes a position in the *previous* list is dropped. A search
   * term, a page and a sort column all mean something different on the other side, and
   * carrying them over shows an empty table for no reason the person can see.
   */
  const handleTabChange = (tabId) => {
    if (tabId === activeTab) return

    // Replace rather than push: the tab is a view of one page, not a place of its own,
    // so Back should leave the Users page rather than walk through the tabs. Edit the
    // existing params rather than replacing them, so switching tabs never drops a
    // parameter that belongs to something else.
    setSearchParams(
      (params) => {
        const next = new URLSearchParams(params)
        if (tabId === USER_TAB.DEALER) next.delete(TAB_PARAM)
        else next.set(TAB_PARAM, tabId)
        return next
      },
      { replace: true },
    )
    setUsers([])
    setSortedColumn('first_name')
    setSearchTerm('')
    setFilter(null)
    if (searchInputRef.current) searchInputRef.current.value = ''
    filterUsers.cancel()
  }

  // Every dependency here reloads the list from page one — the tab, a search, the sort
  // column, the page size. So the page state resets here too, in one place, rather than
  // in each handler: the component's own number, and the table's internal paginator,
  // which nothing else touches. Left alone it would sit on page 4 of a list that just
  // reloaded at page 1, and hand `handlePagination` a page it had already left.
  useEffect(() => {
    setCurrentPage(1)
    setResetPagination((reset) => !reset)
    getUsersList(
      `${USERS_BASE_URL}?${listParams}&page=1&limit=${perPage}&order_by=${sortedColumn}${
        filter ? `&${filter}` : ''
      }&${listExpand}`,
    )
  }, [activeTab, filter, perPage, sortedColumn, type, updateTrigger])

  const renderActionsCell = (row) => (
    <div className="flex flex-row items-center gap-1">
      {canStartViewAs && canViewAsUser(user, row) && (
        <RowActionButton
          disabled={impersonating}
          icon={<EyeIcon className="h-4 w-4" />}
          label={`View as ${displayNameFor(row)}`}
          onClick={() => viewAs(row.id, displayNameFor(row))}
        />
      )}
      {canManageDealerUsers && !isWpcTab && (
        <>
          <RowActionButton
            icon={<PencilIcon className="h-4 stroke-white" />}
            label={`Edit ${displayNameFor(row)}`}
            onClick={() => {
              setSelectedUser(row)
              setIsDealerUserModalOpen(true)
            }}
          />
          <RowActionButton
            icon={<TrashIcon className="h-4 stroke-white" />}
            label={`Delete ${displayNameFor(row)}`}
            onClick={() => {
              setSelectedUser(row)
              setIsDeleteModalOpen(true)
            }}
          />
        </>
      )}
    </div>
  )

  const renderDealerLocationCell = (row) => {
    const getDealerInfo = (dealerUser) => {
      if (dealerUser.dealerUserProfile) {
        const { dealerGroups, dealers } = dealerUser.dealerUserProfile

        if (dealers && dealers.length > 0) {
          return dealers.map((dealer) => dealer.name)
        }

        // No explicit dealers = full group access
        if (dealerGroups && dealerGroups.length > 0) {
          return dealerGroups.map((group) => `${group.name} (All locations)`)
        }

        return ['Location not provided']
      }

      return ['Location not provided']
    }

    return (
      <div className="flex flex-row items-center">
        <MapPinIcon className="stroke-charcoal-900 h-4 w-4 shrink-0" />
        <div className="ml-2">
          {getDealerInfo(row).map((location) => (
            <p key={`${row.id}-${location}`}>{location}</p>
          ))}
        </div>
      </div>
    )
  }

  const NAME_COLUMN = {
    id: 'firstName',
    name: 'Name',
    selector: (row) =>
      !row.firstName && !row.lastName ? 'Name not provided' : `${row.firstName} ${row.lastName}`,
    cell: (row) => (
      <span>
        {!row.firstName && !row.lastName
          ? 'Name not provided'
          : `${row.firstName} ${row.lastName}`}
      </span>
    ),
    sortable: true,
    sortBy: 'first_name',
  }

  const EMAIL_COLUMN = {
    id: 'email',
    name: 'Email',
    cell: (row) => <span>{row.email || 'Email not provided'}</span>,
    selector: (row) => row.email || 'Email not provided',
    sortable: true,
    sortBy: 'email',
  }

  const LAST_LOGIN_COLUMN = {
    id: 'lastLogin',
    name: 'Last Login',
    sortBy: 'last_login',
    sortable: true,
    selector: (row) => formatDate(row.lastLogin),
  }

  const LAST_ACTIVE_COLUMN = {
    id: 'lastActive',
    name: 'Last Active',
    sortBy: 'last_active',
    sortable: true,
    selector: (row) => formatDateTime(row.lastActive),
  }

  // Sizing belongs to the list, not to the column: the two lists hold different columns,
  // so the same content needs a different share of the row on each.
  //
  // How the table divides a row: a column given a `width` is pinned and never grows or
  // shrinks; one given only a `minWidth` splits the leftover space evenly with the other
  // unpinned columns. So dates are pinned at a width their content fits into, and the
  // columns holding free text grow.
  //
  // Dates are pinned wide enough for the value *and* the uppercase header above it, so
  // neither is ever clipped. Everything else shares what is left, in proportion to how
  // much text it holds: `grow` is the share, `minWidth` the floor it never drops below.
  //
  // Weights rather than fixed widths or caps, because one column has to absorb whatever
  // the pinned ones leave behind. Cap them all and the row ends short of the table edge;
  // leave one uncapped and it swallows the lot, which is the whole reason the name column
  // used to run half the width of the screen.
  const NAME_SIZE = { minWidth: '170px', grow: 2 }
  const EMAIL_SIZE = { minWidth: '240px', grow: 3 }
  // Floor fits the longest role on either list — "Administrator", "Management".
  const ROLE_SIZE = { minWidth: '145px', grow: 1 }
  const DATE_WIDTH = '140px'
  const DATE_TIME_WIDTH = '200px'

  const DEALER_COLUMNS = [
    { ...NAME_COLUMN, ...NAME_SIZE },
    { ...EMAIL_COLUMN, ...EMAIL_SIZE },
    {
      id: 'dealerGroup',
      name: 'Dealer Locations',
      cell: renderDealerLocationCell,
      sortable: false,
      minWidth: '220px',
      grow: 3,
    },
    {
      id: 'role',
      name: 'Role',
      selector: (row) => row?.dealerUserProfile?.role || 'N/A',
      sortBy: 'dealer_user_profile__role',
      sortable: true,
      ...ROLE_SIZE,
    },
    { ...LAST_LOGIN_COLUMN, width: DATE_WIDTH },
    { ...LAST_ACTIVE_COLUMN, width: DATE_TIME_WIDTH },
    {
      id: 'createdAt',
      name: 'Created At',
      sortBy: 'created_at',
      sortable: true,
      selector: (row) => formatDate(row.createdAt),
      width: DATE_WIDTH,
    },
  ]

  const WPC_COLUMNS = [
    { ...NAME_COLUMN, ...NAME_SIZE },
    { ...EMAIL_COLUMN, ...EMAIL_SIZE },
    {
      id: 'wpcRole',
      name: 'Role',
      selector: (row) => row?.ssoUserProfile?.role || 'N/A',
      sortBy: 'sso_user_profile__role',
      sortable: true,
      ...ROLE_SIZE,
    },
    {
      id: 'dealerAccess',
      name: 'Dealer Access',
      cell: (row) => (
        <DealerAccessCell
          dealerAccess={row.dealerAccessSummary}
          role={row?.ssoUserProfile?.role}
        />
      ),
      sortable: false,
      // Floor is set by the header, which is wider than any value under it.
      minWidth: '150px',
      grow: 1,
    },
    { ...LAST_LOGIN_COLUMN, width: DATE_WIDTH },
    { ...LAST_ACTIVE_COLUMN, width: DATE_TIME_WIDTH },
  ]

  const COLUMNS = isWpcTab ? [...WPC_COLUMNS] : [...DEALER_COLUMNS]

  // The column carries two independently gated actions: edit/delete for people who
  // manage dealer users, and "View as" for people who may impersonate. Either one alone
  // is reason enough to render it — but neither means an empty column, which is what a
  // read-only session would otherwise get. The WPC list is read-only, so only "View as"
  // reaches it.
  if (canStartViewAs || (canManageDealerUsers && !isWpcTab)) {
    COLUMNS.push({
      id: 'actions',
      name: 'Actions',
      cell: renderActionsCell,
      // The dealer list carries up to three icons, the read-only WPC list carries one.
      // Sized to the icons rather than to the header, so none is ever half cut off.
      width: isWpcTab ? '90px' : '130px',
    })
  }

  return (
    <>
      <div className="mb-4 flex items-center justify-between">
        <h5>Users</h5>
        {!isWpcTab && (
          <div className="flex items-center gap-2">
            <Button
              className="px-1 py-2"
              disabled={loadingExport || isImpersonating}
              label={<DownloadIcon className="h-4 stroke-[#ffffff]" />}
              ariaLabel="Export Users"
              onClick={async () => {
                track(EXPORT_INITIATED, { type: 'user' })
                const params = filter ? `&${filter}` : ''
                const exportTask = await createUserExport(
                  `/user-exports/?${listParams}${params}`,
                  handleError,
                  setLoadingExport,
                )

                if (exportTask) {
                  createNewTask('Download', exportTask, {
                    label: 'Dealer Users',
                    filename: `Dealer_Users_${new Date().toISOString().slice(0, 10)}.xlsx`,
                    checkUpdatedTask: async () => {
                      const updatedTask = await getUserExport(exportTask.id, handleError)
                      return updatedTask
                    },
                  })
                }
              }}
              size="sm"
            />
            {canManageDealerUsers && (
              <Button
                className="self-end"
                icon={<PlusIcon className="h-5 stroke-white" />}
                label="Add Dealer User"
                onClick={() => {
                  setSelectedUser(null)
                  setIsDealerUserModalOpen(true)
                }}
                size="sm"
              />
            )}
          </div>
        )}
      </div>

      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <UserTabs
          activeTab={activeTab}
          onChange={handleTabChange}
          panelId={USERS_PANEL_ID}
          tabs={TABS}
        />

        <TextInput
          className="w-72 pl-10"
          icon={<MagnifyingGlassIcon className="text-gray-dark ml-2 h-5" aria-hidden="true" />}
          id="search"
          endIcon={
            searchTerm ? (
              <button
                type="button"
                onClick={() => {
                  setSearchTerm('')
                  filterUsers('')
                  searchInputRef.current.value = ''
                }}
              >
                <XMarkIcon className="text-gray-dark mr-2 h-5" aria-hidden="true" />
              </button>
            ) : null
          }
          name="search"
          onChange={(e) => {
            filterUsers(e.target.value)
            setSearchTerm(e.target.value)
          }}
          placeholder="Looking for something?"
          ref={searchInputRef}
          value={searchTerm}
        />
      </div>

      <div id={USERS_PANEL_ID} role="tabpanel">
        <DataTable
          columns={COLUMNS}
          data={users}
          pagination
          paginationServer
          paginationPerPage={perPage}
          paginationRowsPerPageOptions={[5, 10, 20, 30, 50]}
          paginationResetDefaultPage={resetPagination}
          paginationTotalRows={totalRows}
          progressPending={loading}
          onChangeRowsPerPage={(currentRowsPerPage) => setPerPage(currentRowsPerPage)}
          onSort={(column, direction) => {
            const d = direction === 'asc' ? '' : '-'
            setSortedColumn(`${d}${column.sortBy}`)
          }}
          onChangePage={(page) =>
            handlePagination(
              page,
              currentPage,
              perPage,
              totalRows,
              pages,
              setCurrentPage,
              getUsersList,
              `${USERS_BASE_URL}?${listParams}&limit=`,
              `order_by=${sortedColumn}&${listExpand}${filter ? `&${filter}` : ''}`,
            )
          }
          sortServer
        />
      </div>

      <DeleteUserModal
        setIsOpen={setIsDeleteModalOpen}
        isOpen={isDeleteModalOpen}
        user={selectedUser}
        onDelete={() => setUpdateTrigger(updateTrigger + 1)}
      />

      {isDealerUserModalOpen && (
        <DealerUserModal
          setIsOpen={setIsDealerUserModalOpen}
          isOpen={isDealerUserModalOpen}
          user={selectedUser}
          onSave={() => setUpdateTrigger(updateTrigger + 1)}
          dealerGroupOptions={dealerGroupOptions?.map((group) => ({
            ...group,
            label: group.name,
          }))}
        />
      )}
    </>
  )
})

export default Users
